import React, { useState, useEffect } from 'react';
import { Container, IconButton, Box, Button, Modal, Card, Typography, TextField } from '@mui/material';
import DataTable from '../components/DataTable';
import PageTitle from '../components/PageTitle';
import { useConfirm } from '../assets/contexts/confirmContext';
import { useNotification } from '../assets/contexts/notificationContext';
import Loader from '../components/loader';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ACTION_ICON_COLORS } from '../constants/iconActionColors';
import * as endPoints from '../assets/js/endpoints';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { INTRANET_PAGE_CONTAINER_SX } from '../constants/layout';

const ManageCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({ name: '' });
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { showNotification } = useNotification();

  const fetchCategories = async () => {
    try {
      const data = await endPoints.getAllCategories();
      setCategories(Array.isArray(data) ? data : data.data);
    } catch (_err) {
      showNotification('Error al obtener categorías', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleDelete = async id => {
    const ok = await confirm('¿Eliminar categoría?');
    if (!ok) return;
    try {
      await endPoints.deleteCategory(id);
      fetchCategories();
      showNotification('Categoría eliminada', 'success');
    } catch (_err) {
      showNotification('Error eliminando categoría', 'error');
    }
  };

  const handleEdit = async id => {
    setEditingId(id);
    setModalOpen(true);
    setFormLoading(true);
    try {
      const data = await endPoints.getCategory(id);
      setForm({ name: data.name || '' });
    } catch (_err) {
      showNotification('Error obteniendo categoría', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreate = () => navigate('/admin/createCategory');

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await endPoints.updateCategory(editingId, form);
      fetchCategories();
      setModalOpen(false);
      setEditingId(null);
      showNotification('Categoría actualizada', 'success');
    } catch (_err) {
      showNotification('Error actualizando categoría', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  if (loading) return <Loader />;

  const columns = [
    { id: 'name', label: 'Nombre' },
  ];

  const actions = category => (
    <>
      <IconButton onClick={() => handleEdit(category.id)} sx={{ color: ACTION_ICON_COLORS.edit }}>
        <EditIcon />
      </IconButton>
      <IconButton onClick={() => handleDelete(category.id)} sx={{ color: ACTION_ICON_COLORS.delete }}>
        <DeleteIcon />
      </IconButton>
    </>
  );

  return (
    <>
      <Helmet>
        <title>Gestión de Categorías | CES</title>
      </Helmet>
      <Container maxWidth="xl" sx={INTRANET_PAGE_CONTAINER_SX}>
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2, flexWrap:'wrap' }}>
          <PageTitle>Gestión de Categorías</PageTitle>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>Crear Categoría</Button>
        </Box>
        <DataTable columns={columns} rows={categories} actions={actions} />
        <Modal
        open={modalOpen}
        onClose={handleClose}
        slotProps={{ backdrop:{ onClick: handleClose } }}
      >
        <Card onClick={e => e.stopPropagation()} sx={{ p:2, m:'5% auto', width:{ xs:'90%', sm:400 } }}>
          <Typography variant='h6' sx={{ textAlign:'center', p:2 }}>Editar Categoría</Typography>
          {formLoading ? (
            <Loader />
          ) : (
            <Box component='form' onSubmit={handleSubmit} sx={{ display:'flex', flexDirection:'column', gap:2 }}>
              <TextField label='Nombre' name='name' value={form.name} onChange={handleChange} required />
              <Box sx={{ display:'flex', gap:2, justifyContent:'center' }}>
                <Button onClick={handleClose}>Cancelar</Button>
                <Button type='submit' variant='contained'>Actualizar</Button>
              </Box>
            </Box>
          )}
        </Card>
      </Modal>
      </Container>
    </>
  );
};

export default ManageCategories;
